/**
 * Document workspaces for the in-app AI tools (card 5.4) — the same wire form
 * MCP takes (card 4.4): `personal`, `personal/<workspaceId>`, `team:<teamId>`.
 * Kept out of `useAiExtensionActions.js` (megafile: it gets a call site, not a
 * feature).
 *
 * Parity with the MCP handlers on purpose: a root create names its workspace
 * with `scope`; a nested create follows its parent (a child belongs to its
 * root's workspace); `get_documents` says which workspace each node is in.
 */
import { listScopes as listWorkspaces } from '../../extensions/bundled/documents/documentsScopes';
import { isWireScope, toDocScope, workspaceIdOf, WORKSPACE_PREFIX, PERSONAL } from '../../mcp/scopes';

/** id → name of the user's workspaces; empty when they cannot be read. */
export async function workspaceNames() {
  try {
    return new Map((await listWorkspaces()).map((w) => [w.id, w.name]));
  } catch {
    return new Map();
  }
}

/**
 * `{ scope, workspace }` for one listed node of `descriptor`'s tree: the wire
 * scope it lives in and, for a workspace, its name.
 */
export function nodeWorkspace(node, descriptor, names) {
  if (descriptor?.type === 'team') return { scope: `team:${descriptor.teamId}`, workspace: null };
  if (!node?.scopeId) return { scope: PERSONAL, workspace: null };
  return { scope: `${WORKSPACE_PREFIX}${node.scopeId}`, workspace: names.get(node.scopeId) || null };
}

/**
 * The store descriptor a ROOT create lands in, from the tool's `scope` input:
 * `{ scope }`, or `{ error }` (a malformed scope or a workspace that does not
 * exist). A team root stays out, as before this card: a team document starts
 * inside an existing team folder (`parent_id`), which carries its scope.
 */
export async function rootCreateScope(wire) {
  if (wire == null || wire === '' || wire === PERSONAL) return { scope: { type: 'personal' } };
  const workspace = isWireScope(wire) ? workspaceIdOf(wire) : null;
  if (!workspace) return { error: 'scope must be personal or personal/<workspaceId> (see get_documents)' };
  const names = await workspaceNames();
  return names.has(workspace) ? { scope: toDocScope(wire) } : { error: 'No workspace with that id' };
}
